'use client';

import type { FC } from 'react';
import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { ErrorMessage } from '@/components/ErrorMessage';
import { EmptyState } from '@/components/ui';
import { CaseCard } from '@/components/cases/CaseCard';
import { CaseCardSkeleton } from '@/components/CaseCardSkeleton';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import { useLocalizedHref } from '@/hooks/useLocalizedHref';
import { useCasesQuery } from '@/queries/useCasesQuery';
import { pinForLanguage } from '@/components/cases/catalog/catalogSort';
import translations from './CasesScreenMarketing.translations';

const GRID_CLASS =
  'grid pt-4 grid-cols-[repeat(auto-fill,minmax(min(100%,160px),1fr))] gap-1 lg:grid-cols-4 lg:gap-3';

export const CasesScreenMarketing: FC = () => {
  const { t, lang } = useLocalTranslation(translations);
  const { data: cases = [], isLoading, error, refetch } = useCasesQuery();
  const showSkeleton = isLoading && cases.length === 0;
  const router = useRouter();
  const localize = useLocalizedHref();

  const handleCaseClick = useCallback(
    (caseId: string) => {
      router.push(localize(`/cases/${encodeURIComponent(caseId)}`));
    },
    [router, localize],
  );

  if (showSkeleton) {
    return (
      <div className={GRID_CLASS}>
        {[...Array(8)].map((_, index) => (
          <CaseCardSkeleton key={`skeleton-${index}`} />
        ))}
      </div>
    );
  }

  if (error && cases.length === 0) {
    return (
      <ErrorMessage onRetry={() => refetch()} />
    );
  }

  if (cases.length === 0) {
    return <EmptyState message={t('empty')} />;
  }

  return (
    <div className={GRID_CLASS}>
      {pinForLanguage(cases, lang).map((caseItem) => (
        <CaseCard
          key={caseItem.id}
          id={caseItem.id}
          name={caseItem.name}
          imageUrl={caseItem.imageUrl}
          imagePlaceholder={caseItem.imagePlaceholder}
          priceInCredits={caseItem.priceInCredits}
          openingsCount={caseItem.openingsCount}
          isNew={caseItem.isNew}
          onClick={handleCaseClick}
        />
      ))}
    </div>
  );
};
